import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const redirectTo = `${window.location.origin}/reset-password`;

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

    if (resetError) {
      setError(resetError.message);
      setLoading(false);
      return;
    }

    setSent(true);
    setLoading(false);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        {/* Logo / Branding */}
        <div className="mb-8 text-center">
          <img src="/images/logo-black.svg" alt="Ellie" className="h-10 w-auto mx-auto" />
          <p className="mt-2 text-sm text-text-muted">Admin Dashboard</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border bg-white px-6 py-8 shadow-sm">
          {sent ? (
            <div className="text-center">
              <h2 className="mb-2 text-lg font-semibold text-text-primary">Check your email</h2>
              <p className="mb-6 text-sm text-text-muted">
                We sent a password reset link to{' '}
                <span className="font-medium text-text-primary">{email}</span>. Click the link in
                the email to reset your password.
              </p>
              <Link to="/login" className="text-sm font-medium text-brand hover:text-brand-dark">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h2 className="mb-2 text-center text-lg font-semibold text-text-primary">
                Reset your password
              </h2>
              <p className="mb-6 text-center text-sm text-text-muted">
                Enter your email and we'll send you a reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-text-primary"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
                    placeholder="admin@ellie.app"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <p className="mt-4 text-center text-sm text-text-muted">
                <Link to="/login" className="font-medium text-brand hover:text-brand-dark">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
